import { useCallback, useEffect, useMemo, useState } from 'react';
import { amplitudeService } from 'services/amplitude';

import { closedStorageKey } from './UpgradeExpiredPlanNotification.constants';
import { getCompaniesIdsString, isObjectWithStringValues } from './UpgradeExpiredPlanNotification.helpers';

/**
 * Save, sync, and update the notification state in localStorage for every user profile
 * The data stores like that:
 * {
 *     <profileId>: '<companyId>.<companyId>.<companyId>.<...>', // means that the notification for these companies is closed for this profile
 * }
 *
 * @param companiesIds Array of company ID strings to concatenate
 * @param profileId The profile ID to save the notification for
 */

export const useSaveToLocalStorage = (companiesIds: string[], profileId?: string) => {
    const [closedPromotions, setIsClosedPromotions] = useState<Record<string, string>>(() => {
        const closedPromotions = JSON.parse(localStorage.getItem(closedStorageKey) || '{}');

        return isObjectWithStringValues(closedPromotions) ? closedPromotions : {};
    });

    const closeNotification = useCallback(() => {
        if (profileId) {
            const newClosedPromotions = {
                ...closedPromotions,
                [profileId]: getCompaniesIdsString(companiesIds),
            };

            localStorage.setItem(closedStorageKey, JSON.stringify(newClosedPromotions));

            setIsClosedPromotions(newClosedPromotions);
            amplitudeService.sendData(`Billing: click on "Don't show again" AFT expired`);
        }
    }, [closedPromotions, companiesIds, profileId, setIsClosedPromotions]);

    const isNotificationClosed = useMemo(() => {
        if (profileId && profileId in closedPromotions) {
            return closedPromotions[profileId] === getCompaniesIdsString(companiesIds);
        }

        return false;
    }, [closedPromotions, profileId, companiesIds]);

    useEffect(() => {
        if (!isNotificationClosed && profileId && closedPromotions[profileId]) {
            setIsClosedPromotions((prevState) => {
                const newClosetPromotions = { ...prevState };

                delete newClosetPromotions[profileId];

                localStorage.setItem(closedStorageKey, JSON.stringify(newClosetPromotions));

                return newClosetPromotions;
            });
        }
    }, [isNotificationClosed, profileId, closedPromotions, setIsClosedPromotions]);

    return {
        closeNotification,
        isNotificationClosed,
    };
};
